// core/services/legalFloorService.ts — the legal-floor reporting route.
//
// This is the ONLY report path that does not require an account, and that is
// deliberate: the person best placed to report an intimate image of themselves
// may have no account, someone may be acting for a child, and a legal order
// arrives from a party who is not a member. Requiring a login here would mean
// the platform's most urgent route excluded exactly the people who need it.
//
// Deliberately NOT re-exported from core/services/index.ts. The barrel is for
// services the hooks layer wraps; this one is imported straight into the page
// that owns the form, exactly like appealsService (also public, also no-auth).

import { api } from '@/core/lib/apiClient';

/** The closed list from backend LegalFloorCategory — kept in step with it by hand. */
export const LEGAL_FLOOR_CATEGORIES = [
  {
    id: 'illegal-minor-content',
    label: 'Illegal content involving a minor',
    hint: 'Sexual content involving anyone under 18, or content that sexualises a child.',
  },
  {
    id: 'nonconsensual-intimate',
    label: 'Intimate images shared without consent',
    hint: 'Intimate or sexual images of someone shared without their agreement, including of yourself.',
  },
  {
    id: 'court-order',
    label: 'Valid legal order',
    hint: 'A court order or equivalent legal instruction requiring removal.',
  },
] as const;

export type LegalFloorCategoryId = (typeof LEGAL_FLOOR_CATEGORIES)[number]['id'];

/**
 * How much of the description either reporting surface will take.
 *
 * A client-side cap, not a column width — the server's `detail` is an unbounded
 * TextField. It lives here rather than in a page because there are now two ways
 * in (the account-free /report-illegal page and the in-app report dialog), and
 * two copies of the number would eventually stop matching. Both surfaces show a
 * counter against it: the box stops dead at the limit, and a reporter who is not
 * told cannot tell whether the rest of what they typed was sent.
 */
export const LEGAL_FLOOR_DETAIL_MAX = 4000;

export interface LegalFloorReportInput {
  category: LegalFloorCategoryId;
  /** What and where. Required — the server rejects a blank one. */
  detail: string;
  /** Optional post id, when the reporter has one. */
  targetId?: string;
  /** Optional way to reach them back. Never required: many reporters cannot be contacted. */
  contact?: string;
}

export const legalFloorService = {
  /**
   * Submit a report. Resolves on success and tells you nothing else — the
   * server deliberately returns no id and no status lookup, because a route
   * open to anyone must not double as a way to probe whether a report exists
   * or what happened to it. Throws ApiError on a rejected submission
   * (`validation`, optionally with `fields.detail`); the caller discriminates.
   */
  async report(input: LegalFloorReportInput): Promise<void> {
    await api('/legal-floor/report/', {
      method: 'POST',
      body: {
        category: input.category,
        detail: input.detail.trim(),
        targetId: input.targetId?.trim() || '',
        contact: input.contact?.trim() || '',
      },
    });
  },
} as const;
